import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowUpRight,
  Check,
  X,
  Phone,
  ShieldCheck,
  Sun,
  Droplets,
  Bug,
  Leaf,
  ScanLine,
  Sparkles,
  FileCheck,
  Layers,
  ClipboardCheck,
  Plus,
  Minus,
  Star,
  Gem,
  Award,
  AlertTriangle,
} from "lucide-react";
import SEO from "@/components/SEO";
import QuoteForm from "@/components/QuoteForm";
import { COMPANY } from "@/data";

const scrollToQuoteForm = (e) => {
  e.preventDefault();
  document.querySelector('[data-testid="quote-form"]')?.scrollIntoView({ behavior: "smooth", block: "center" });
};

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.8, delay: i * 0.06, ease: [0.22, 1, 0.36, 1] },
  }),
};

/* ============ DATA ============ */

const THREATS = [
  {
    icon: Sun,
    title: "Rayons UV",
    text: "Décoloration du vernis, ternissement progressif, oxydation. En Belgique, même sous nos ciels gris, les UV traversent et attaquent la peinture toute l'année.",
  },
  {
    icon: Droplets,
    title: "Calcaire & pluie acide",
    text: "L'eau qui sèche laisse des dépôts calcaires qui, avec le temps, cristallisent dans le vernis. Chaque pluie chargée en polluants dégrade la surface.",
  },
  {
    icon: Bug,
    title: "Fientes d'oiseaux & insectes",
    text: "Acides organiques particulièrement corrosifs : en 24 à 48h, ils peuvent marquer définitivement le vernis d'origine s'ils ne sont pas nettoyés à temps.",
  },
  {
    icon: Leaf,
    title: "Sève, résine, pollens",
    text: "Sous la chaleur, ces éléments naturels se fixent et attaquent le vernis. Ils demandent un décollement chimique agressif pour être retirés.",
  },
  {
    icon: ScanLine,
    title: "Micro-rayures de lavage",
    text: "Chaque passage au rouleau de station, chaque éponge non-adaptée crée des micro-rayures circulaires (swirls) qui ternissent la profondeur de la couleur.",
  },
  {
    icon: AlertTriangle,
    title: "Sel de déneigement & retombées industrielles",
    text: "En hiver et en zone urbaine, les particules ferreuses et le sel s'incrustent dans le vernis, accélèrent l'oxydation et créent des points de corrosion.",
  },
];

const METHOD_STEPS = [
  {
    n: "01",
    icon: Droplets,
    title: "Décontamination complète",
    text: "Lavage main pH neutre, décontamination chimique (fer-out), décontamination mécanique à l'argile. On retire tout ce qui s'est incrusté dans le vernis au fil des années.",
    duration: "2 à 3h",
  },
  {
    n: "02",
    icon: Layers,
    title: "Correction du vernis",
    text: "Polissage 1 à 3 étapes selon l'état, sous éclairage LED d'inspection. On corrige avant de figer : sans cette étape, la céramique fige les défauts pour toute sa durée de vie.",
    duration: "3 à 12h",
  },
  {
    n: "03",
    icon: Sparkles,
    title: "Dégraissage IPA & application",
    text: "Nettoyage IPA pour retirer tout résidu d'huile de polissage. Application panneau par panneau, à la main, en atmosphère contrôlée (température, hygrométrie).",
    duration: "3 à 5h",
  },
  {
    n: "04",
    icon: ClipboardCheck,
    title: "Cure & contrôle qualité",
    text: "Cure de 24 à 48h en atelier climatisé. Inspection finale, remise du certificat de garantie écrit et briefing d'entretien personnalisé.",
    duration: "24 à 48h",
  },
];

const TIERS = [
  {
    id: "1an",
    name: "1 an",
    tag: "Découverte",
    subtitle: "L'entrée de gamme pour tester la protection céramique.",
    price: "490",
    supplement: { break: "+100€", suv: "+200€" },
    highlighted: false,
    features: [
      "Décontamination complète",
      "Polissage One-Step (correction 70%)",
      "Application céramique 1 an",
      "Effet hydrophobe & anti-UV",
      "Certificat de garantie écrit",
      "Kit d'entretien offert",
    ],
  },
  {
    id: "3ans",
    name: "3 ans",
    tag: "Le meilleur choix",
    subtitle: "Le juste équilibre entre budget, durabilité et rendu esthétique.",
    price: "790",
    supplement: { break: "+130€", suv: "+260€" },
    highlighted: true,
    ribbon: "Le meilleur rapport",
    features: [
      "Décontamination complète",
      "Polissage 2 étapes (correction 90%)",
      "Application céramique 3 ans",
      "Effet hydrophobe renforcé",
      "Résistance chimique élevée",
      "Certificat de garantie écrit",
      "Kit d'entretien offert",
      "1 contrôle annuel offert",
    ],
  },
  {
    id: "5ans",
    name: "5 ans",
    tag: "Protection maximale",
    subtitle: "La protection la plus longue disponible, avec préparation concours et passe de finition.",
    price: "1090",
    supplement: { break: "+200€", suv: "+400€" },
    highlighted: false,
    features: [
      "Décontamination complète",
      "Polissage 3 étapes (correction 95%+)",
      "Passe de finition incluse (jewelling)",
      "Application céramique 5 ans",
      "Effet hydrophobe maximal",
      "Résistance chimique premium",
      "Certificat de garantie écrit",
      "Kit d'entretien Pro offert",
      "2 contrôles annuels offerts",
    ],
  },
];

const TRUTH = [
  { label: "Protège du contact UV", isTrue: true },
  { label: "Repousse l'eau, la saleté, les projections routières", isTrue: true },
  { label: "Facilite radicalement le lavage", isTrue: true },
  { label: "Résiste aux fientes, sève, sel", isTrue: true },
  { label: "Intensifie la brillance et la profondeur", isTrue: true },
  { label: "Empêche les rayures profondes (portière, branche)", isTrue: false },
  { label: "Corrige les rayures et défauts existants", isTrue: false },
  { label: "Dispense de tout entretien du véhicule", isTrue: false },
  { label: "Élimine les dommages internes du vernis", isTrue: false },
];

const GUARANTEE = [
  {
    icon: FileCheck,
    title: "Certificat écrit remis en main propre",
    text: "À la fin de la prestation, vous repartez avec un certificat nominatif : produit utilisé, référence du lot, durée de garantie, conditions d'entretien. Un standard que peu d'ateliers appliquent réellement.",
  },
  {
    icon: ShieldCheck,
    title: "Garantie fabricant + garantie atelier",
    text: "Nous utilisons des produits certifiés par leur fabricant (Gtechniq, Gyeon, CarPro). La garantie est valable sur toute la durée annoncée, sous conditions d'entretien.",
  },
  {
    icon: ClipboardCheck,
    title: "Contrôle annuel en atelier",
    text: "Le seul moyen de garantir la durée de vie d'une céramique : un contrôle en atelier chaque année. Nous mesurons l'effet hydrophobe et rechargons si nécessaire. C'est ce qui distingue une protection sérieuse d'une promesse marketing.",
  },
];

const FAQ = [
  {
    q: "Quelle est la vraie durée de vie d'une céramique ?",
    a: "La durée annoncée (1, 3 ou 5 ans) correspond à la durée MAXIMALE dans des conditions d'usage et d'entretien normales. Sans entretien, cette durée peut chuter de moitié. C'est pour cette raison que nous conditionnons la garantie à un contrôle annuel en atelier : c'est le seul moyen honnête de tenir la promesse initiale.",
  },
  {
    q: "Quel entretien après une céramique ?",
    a: "Aucun entretien miraculeux, mais des règles simples : lavage main avec shampoing pH neutre uniquement (jamais de station à rouleaux), micro-fibres propres, séchage soigné. Éviter les produits agressifs (nettoyants vitres classiques sur la carrosserie, cires enrichies). Un booster hydrophobe tous les 4 à 6 mois est recommandé (fourni dans nos kits d'entretien).",
  },
  {
    q: "Puis-je passer en station-service après ?",
    a: "Non pour les stations à rouleaux : les brosses créent des micro-rayures qui, à terme, dégradent la couche céramique. Oui pour les stations haute-pression sans contact (aire de lavage) : c'est même idéal, car l'effet hydrophobe rend le nettoyage rapide et efficace, sans contact abrasif.",
  },
  {
    q: "Combien de temps immobilisez-vous mon véhicule ?",
    a: "Cela dépend de la formule : 1 an compter 2 jours (préparation + cure), 3 ans compter 2 à 3 jours (correction plus poussée), 5 ans compter 3 à 4 jours (préparation concours + cure prolongée). Nous vous communiquons un créneau précis à la réservation.",
  },
  {
    q: "Quelle différence entre une cire, un sealant et une céramique ?",
    a: "Une cire (carnauba) dure 6 à 12 semaines : c'est un consommable. Un sealant synthétique dure 6 à 9 mois : c'est déjà mieux. Une céramique dure 1 à 5 ans : elle crée une véritable couche cristalline (dureté 9H) qui se lie chimiquement au vernis. C'est un investissement à long terme, pas un consommable.",
  },
  {
    q: "Pourquoi la préparation est-elle indispensable ?",
    a: "Parce qu'une céramique ne corrige rien : elle fige l'état existant du vernis pour toute sa durée de vie. Si vous appliquez une céramique sur une peinture rayée, oxydée ou contaminée, vous emprisonnez ces défauts pour 3 ou 5 ans. C'est ce qui différencie une offre à 300€ (application seule) d'une prestation professionnelle à 790€ (décontamination + correction + application). L'économie initiale devient un regret esthétique durable.",
  },
];

/* ============ COMPONENT ============ */
const Ceramique = () => (
  <>
    <SEO
      title="Traitement Céramique Charleroi | Protection Voiture Belgique | VistaWash"
      description="Traitement céramique Charleroi & Châtelet : décontamination, correction peinture et protection professionnelle. Detailing premium à Châtelet. Gammes 1, 3 et 5 ans, certificat écrit."
    />
    <Hero />
    <ThreatsSection />
    <TruthSection />
    <MethodSection />
    <TiersSection />
    <GuaranteeSection />
    <FaqSection />
    <FinalCTA />
  </>
);

/* ============ HERO ============ */
const Hero = () => (
  <section
    data-testid="ceram-hero"
    className="relative min-h-[92vh] flex items-center pt-40 pb-24 grain bg-black"
  >
    <div className="absolute inset-0 z-0">
      <img
        src="https://images.unsplash.com/photo-1618843479313-40f8afb4b4d8?auto=format&fit=crop&w=1920&q=90"
        alt="Traitement céramique Charleroi VistaWash Châtelet"
        className="w-full h-full object-cover hero-zoom"
      />
      <div className="absolute inset-0 bg-gradient-to-r from-black via-black/85 to-black/30" />
      <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-black/50" />
    </div>

    <div className="relative z-10 max-w-[1500px] mx-auto px-6 md:px-12 w-full">
      <motion.div initial="hidden" animate="visible" variants={fadeUp} className="flex items-center gap-3 mb-6">
        <div className="w-12 h-px bg-[#D90000]" />
        <span className="text-[11px] uppercase tracking-[0.35em] text-[#D90000]">
          Traitement céramique · Charleroi & Châtelet
        </span>
      </motion.div>

      <motion.h1
        initial="hidden"
        animate="visible"
        variants={fadeUp}
        custom={1}
        className="font-impact text-5xl sm:text-6xl md:text-7xl lg:text-[96px] leading-[0.9] max-w-5xl"
      >
        UNE PROTECTION <br />
        QUI TIENT <span className="text-[#D90000]">CE QU'ELLE PROMET.</span>
      </motion.h1>

      <motion.p
        initial="hidden"
        animate="visible"
        variants={fadeUp}
        custom={2}
        className="text-white/75 text-base md:text-lg max-w-2xl mt-8 leading-relaxed"
      >
        Chez VistaWash, un traitement céramique ne se résume pas à une application. C'est une préparation
        complète du vernis suivie d'une protection professionnelle, garantie par certificat écrit. La référence
        en protection chimique longue durée pour préserver la beauté et la valeur de votre véhicule.
      </motion.p>

      <motion.div initial="hidden" animate="visible" variants={fadeUp} custom={3} className="flex flex-wrap gap-4 mt-10">
        <a
          href="#contact"
          onClick={scrollToQuoteForm}
          data-testid="ceram-hero-quote-cta"
          className="inline-flex items-center gap-3 bg-[#D90000] hover:bg-white hover:text-black text-white text-[12px] uppercase tracking-[0.22em] font-bold px-8 py-5 transition-all border border-[#D90000] hover:border-white"
        >
          Demander un devis <ArrowUpRight size={16} />
        </a>
        <a
          href="#formules"
          data-testid="ceram-hero-tiers-cta"
          className="inline-flex items-center gap-3 border border-white/30 hover:border-white hover:bg-white hover:text-black text-white text-[12px] uppercase tracking-[0.22em] font-bold px-8 py-5 transition-all"
        >
          Voir les tarifs
        </a>
      </motion.div>

      <motion.div
        initial="hidden"
        animate="visible"
        variants={fadeUp}
        custom={4}
        className="mt-16 flex flex-wrap items-center gap-6 text-[11px] uppercase tracking-[0.22em] text-white/50"
      >
        <div className="flex items-center gap-2">
          <div className="flex gap-0.5 text-[#FBBC04]">
            {[...Array(5)].map((_, j) => (
              <Star key={j} size={14} fill="currentColor" stroke="none" />
            ))}
          </div>
          <span>5,0 · 35 avis Google</span>
        </div>
        <div className="flex items-center gap-2">
          <FileCheck size={14} className="text-[#D90000]" /> Certificat de garantie remis
        </div>
        <div className="flex items-center gap-2">
          <Award size={14} className="text-[#D90000]" /> Detailing premium Châtelet
        </div>
      </motion.div>
    </div>
  </section>
);

/* ============ THREATS (le problème) ============ */
const ThreatsSection = () => (
  <section data-testid="ceram-threats" className="py-24 md:py-32 px-6 md:px-12 bg-[#0a0a0a]">
    <div className="max-w-[1500px] mx-auto">
      <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} className="max-w-3xl mb-16">
        <span className="text-[11px] uppercase tracking-[0.3em] text-[#D90000]">— Le problème</span>
        <h2 className="font-impact text-5xl md:text-6xl lg:text-[80px] leading-[0.9] mt-6">
          CE QUE SUBIT UNE PEINTURE <br />
          <span className="text-[#D90000]">NON PROTÉGÉE.</span>
        </h2>
        <p className="text-white/60 mt-8 text-base md:text-lg leading-relaxed">
          Une peinture automobile est une surface vivante. Chaque jour, six agressions distinctes attaquent le vernis.
          Sans protection, ces dégâts sont invisibles au début, puis irréversibles. À terme, ils coûtent bien plus
          cher qu'une protection préventive.
        </p>
      </motion.div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {THREATS.map((t, i) => {
          const Icon = t.icon;
          return (
            <motion.div
              key={t.title}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeUp}
              custom={i}
              data-testid={`ceram-threat-${i}`}
              className="p-8 border border-white/10 bg-[#050505] hover:border-[#D90000]/50 transition-colors"
            >
              <Icon size={28} className="text-[#D90000] mb-5" />
              <h3 className="font-impact text-2xl mb-3">{t.title}</h3>
              <p className="text-white/60 text-sm leading-relaxed">{t.text}</p>
            </motion.div>
          );
        })}
      </div>
    </div>
  </section>
);

/* ============ TRUTH (ce que la céramique est / n'est pas) ============ */
const TruthSection = () => (
  <section data-testid="ceram-truth" className="py-24 md:py-32 px-6 md:px-12">
    <div className="max-w-[1500px] mx-auto">
      <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} className="max-w-3xl mb-16">
        <span className="text-[11px] uppercase tracking-[0.3em] text-[#D90000]">— Mythes & réalités</span>
        <h2 className="font-impact text-5xl md:text-6xl lg:text-[80px] leading-[0.9] mt-6">
          CE QU'EST UNE CÉRAMIQUE. <br />
          ET CE QU'ELLE <span className="text-[#D90000]">N'EST PAS.</span>
        </h2>
        <p className="text-white/60 mt-8 text-base md:text-lg leading-relaxed">
          Comprendre exactement ce qu'apporte une céramique évite les déceptions. Un revêtement céramique
          est une couche cristalline qui se lie chimiquement au vernis pour le protéger. Elle ne le répare pas,
          elle le préserve.
        </p>
      </motion.div>

      <div className="grid md:grid-cols-2 gap-4 md:gap-6">
        <div className="border border-[#D90000]/40 bg-gradient-to-b from-[#1a0505] to-[#0a0a0a] p-8 md:p-10">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-[#D90000] flex items-center justify-center">
              <Check size={20} className="text-white" />
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-[0.28em] text-[#D90000] font-bold">Ce qu'elle fait vraiment</div>
              <div className="font-impact text-2xl md:text-3xl">CE QUI EST VRAI</div>
            </div>
          </div>
          <ul className="space-y-4">
            {TRUTH.filter((t) => t.isTrue).map((t) => (
              <li key={t.label} className="flex items-start gap-3 text-white/90 text-sm md:text-base leading-relaxed">
                <Check size={18} className="text-[#D90000] mt-0.5 shrink-0" />
                <span>{t.label}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="border border-white/10 bg-[#050505] p-8 md:p-10">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-white/10 flex items-center justify-center">
              <X size={20} className="text-white/70" />
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-[0.28em] text-white/45 font-bold">Ce qu'elle ne fait pas</div>
              <div className="font-impact text-2xl md:text-3xl">LES IDÉES REÇUES</div>
            </div>
          </div>
          <ul className="space-y-4">
            {TRUTH.filter((t) => !t.isTrue).map((t) => (
              <li key={t.label} className="flex items-start gap-3 text-white/70 text-sm md:text-base leading-relaxed">
                <X size={18} className="text-white/40 mt-0.5 shrink-0" />
                <span>{t.label}</span>
              </li>
            ))}
          </ul>
          <p className="mt-8 pt-6 border-t border-white/10 text-white/50 text-xs italic leading-relaxed">
            Une céramique fige l'état du vernis au moment de l'application. C'est pourquoi la préparation
            (décontamination + correction) est aussi importante que la céramique elle-même.
          </p>
        </div>
      </div>
    </div>
  </section>
);

/* ============ METHOD ============ */
const MethodSection = () => (
  <section data-testid="ceram-method" className="py-24 md:py-32 px-6 md:px-12 bg-[#0a0a0a]">
    <div className="max-w-[1500px] mx-auto">
      <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} className="max-w-3xl mb-16">
        <span className="text-[11px] uppercase tracking-[0.3em] text-[#D90000]">— Notre méthode</span>
        <h2 className="font-impact text-5xl md:text-6xl lg:text-[80px] leading-[0.9] mt-6">
          QUATRE ÉTAPES. <br />
          <span className="text-[#D90000]">AUCUN RACCOURCI.</span>
        </h2>
        <p className="text-white/60 mt-8 text-base md:text-lg leading-relaxed">
          C'est cette méthode qui différencie une offre à 300€ d'une protection professionnelle qui tient
          sur la durée. Chaque étape a son rôle. En sauter une, c'est compromettre le résultat pour des années.
        </p>
      </motion.div>

      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
        {METHOD_STEPS.map((s, i) => {
          const Icon = s.icon;
          return (
            <motion.div
              key={s.n}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeUp}
              custom={i}
              data-testid={`ceram-step-${s.n}`}
              className="relative p-8 border border-white/10 bg-[#050505] hover:border-[#D90000]/50 transition-colors"
            >
              <div className="flex items-start justify-between mb-6">
                <span className="font-impact text-5xl md:text-6xl text-[#D90000] leading-none">{s.n}</span>
                <Icon size={26} className="text-white/40" />
              </div>
              <h3 className="font-impact text-xl md:text-2xl mb-3 leading-tight">{s.title}</h3>
              <p className="text-white/60 text-sm leading-relaxed mb-5">{s.text}</p>
              <div className="pt-4 border-t border-white/10 flex items-center justify-between">
                <span className="text-[10px] uppercase tracking-[0.25em] text-white/40">Durée</span>
                <span className="text-[#D90000] font-bold text-sm">{s.duration}</span>
              </div>
            </motion.div>
          );
        })}
      </div>

      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        variants={fadeUp}
        className="mt-12 p-6 md:p-8 border border-[#D90000]/30 bg-[#D90000]/[0.04] flex items-start gap-4"
      >
        <AlertTriangle size={22} className="text-[#D90000] shrink-0 mt-1" />
        <div>
          <div className="text-[11px] uppercase tracking-[0.3em] text-[#D90000] font-bold mb-2">
            À retenir
          </div>
          <p className="text-white/80 leading-relaxed">
            Une céramique posée sans correction du vernis fige les défauts existants pour toute sa durée de vie.
            C'est ce qui explique la différence entre un traitement « rapide » à bas coût et une prestation professionnelle
            à Châtelet. Chez VistaWash, chaque céramique commence par une préparation complète. Sans exception.
          </p>
        </div>
      </motion.div>
    </div>
  </section>
);

/* ============ TIERS ============ */
const TiersSection = () => (
  <section id="formules" data-testid="ceram-tiers" className="py-24 md:py-32 px-6 md:px-12">
    <div className="max-w-[1500px] mx-auto">
      <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} className="text-center mb-16">
        <span className="text-[11px] uppercase tracking-[0.3em] text-[#D90000]">— Nos formules céramique</span>
        <h2 className="font-impact text-5xl md:text-7xl lg:text-[100px] leading-[0.9] mt-6">
          TROIS DURÉES. <br />
          <span className="text-[#D90000]">UN SEUL STANDARD.</span>
        </h2>
        <p className="text-white/60 mt-6 max-w-2xl mx-auto leading-relaxed">
          Chaque formule inclut la préparation complète et la certification écrite.
          Choisissez la durée de protection adaptée à votre usage et à votre budget.
        </p>
      </motion.div>

      <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
        {TIERS.map((t, i) => (
          <motion.article
            key={t.id}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            custom={i}
            data-testid={`ceram-tier-${t.id}`}
            className={`relative flex flex-col p-8 md:p-10 border transition-all ${
              t.highlighted
                ? "border-[#D90000] bg-gradient-to-b from-[#1a0505] to-[#0a0a0a] shadow-2xl shadow-[#D90000]/25 md:scale-[1.05] md:-translate-y-2"
                : "border-white/10 bg-[#0a0a0a] hover:border-white/30"
            }`}
          >
            {t.highlighted && (
              <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-[#D90000] text-white text-[10px] uppercase tracking-[0.28em] font-bold px-5 py-2 whitespace-nowrap">
                ★ {t.ribbon}
              </div>
            )}

            <div className="text-[11px] uppercase tracking-[0.28em] text-[#D90000] font-semibold mb-3">
              {t.tag}
            </div>
            <h3 className="font-impact text-5xl md:text-6xl leading-none mb-4">{t.name}</h3>
            <p className="text-white/70 text-sm mb-8 leading-relaxed min-h-[64px]">{t.subtitle}</p>

            {/* Pricing simple "à partir de" */}
            <div className="mb-8">
              <div className="flex items-baseline gap-2 mb-1">
                <span className="text-white/50 text-sm">À partir de</span>
              </div>
              <div className="flex items-baseline gap-2 mb-2">
                <span className="font-impact text-6xl md:text-7xl text-white leading-none">{t.price}</span>
                <span className="font-impact text-3xl text-[#D90000] leading-none">€</span>
                <span className="text-white/50 text-sm">TTC</span>
              </div>
              <div className="text-white/45 text-xs italic">(pour une citadine)</div>
            </div>

            <ul className="space-y-3 mb-8 flex-1">
              {t.features.map((f) => (
                <li key={f} className="flex items-start gap-3 text-sm text-white/85 leading-relaxed">
                  <Check size={16} className="text-[#D90000] mt-0.5 shrink-0" />
                  <span>{f}</span>
                </li>
              ))}
            </ul>

            <a
              href="#contact"
              data-testid={`ceram-tier-cta-${t.id}`}
              className={`inline-flex items-center justify-center gap-3 w-full text-[12px] uppercase tracking-[0.22em] font-bold px-6 py-4 transition-all border ${
                t.highlighted
                  ? "bg-[#D90000] hover:bg-white hover:text-black text-white border-[#D90000] hover:border-white"
                  : "bg-transparent hover:bg-[#D90000] text-white border-white/25 hover:border-[#D90000]"
              }`}
            >
              Demander un devis <ArrowUpRight size={14} />
            </a>
          </motion.article>
        ))}
      </div>

      {/* Suppléments véhicule (par formule) */}
      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        variants={fadeUp}
        data-testid="ceram-supplements"
        className="mt-10 md:mt-14 border border-white/10 bg-[#0a0a0a] p-6 md:p-8"
      >
        <div className="flex items-center gap-3 mb-5">
          <Plus size={16} className="text-[#D90000]" />
          <div className="text-[11px] uppercase tracking-[0.3em] text-[#D90000] font-bold">
            Suppléments véhicule
          </div>
          <span className="hidden md:inline text-white/45 text-xs italic">Tarif base = citadine</span>
        </div>

        <div className="hidden md:grid grid-cols-[1.2fr_1fr_1fr] gap-3 pb-3 border-b border-white/10">
          <div className="text-[10px] uppercase tracking-[0.25em] text-white/45">Formule</div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-white/45 text-center">Berline / Break</div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-white/45 text-center">SUV / 4x4</div>
        </div>

        {TIERS.map((t) => (
          <div
            key={t.id}
            data-testid={`ceram-supp-${t.id}`}
            className="grid grid-cols-3 md:grid-cols-[1.2fr_1fr_1fr] gap-3 items-center py-4 border-b border-white/10 last:border-b-0"
          >
            <div className="flex items-center gap-2">
              <span className="font-impact text-2xl md:text-3xl text-white leading-none">{t.name}</span>
              <span className="text-[10px] uppercase tracking-[0.22em] text-white/40 hidden md:inline">
                Céramique
              </span>
            </div>
            <div className="text-center">
              <span className="md:hidden text-[9px] uppercase tracking-[0.2em] text-white/40 block mb-1">Berline / Break</span>
              <span className="font-impact text-xl md:text-2xl text-[#D90000]">{t.supplement.break}</span>
            </div>
            <div className="text-center">
              <span className="md:hidden text-[9px] uppercase tracking-[0.2em] text-white/40 block mb-1">SUV / 4x4</span>
              <span className="font-impact text-xl md:text-2xl text-[#D90000]">{t.supplement.suv}</span>
            </div>
          </div>
        ))}
      </motion.div>

      {/* Passe de polissage supplémentaire */}
      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        variants={fadeUp}
        data-testid="ceram-passes"
        className="mt-6 md:mt-8 border border-white/10 bg-[#0a0a0a] p-6 md:p-8"
      >
        <div className="flex items-start md:items-center gap-3 mb-5 flex-col md:flex-row md:justify-between">
          <div className="flex items-center gap-3">
            <Layers size={16} className="text-[#D90000]" />
            <div className="text-[11px] uppercase tracking-[0.3em] text-[#D90000] font-bold">
              Passe de polissage supplémentaire
            </div>
          </div>
          <span className="text-white/45 text-xs italic">
            Uniquement si vernis très dégradé (diagnostic sous LED)
          </span>
        </div>
        <div className="grid sm:grid-cols-3 gap-3 md:gap-4">
          <div className="p-5 border border-white/10 bg-black flex items-center justify-between">
            <div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-white/45 mb-1">Citadine</div>
              <div className="text-white text-sm">Une passe supplémentaire</div>
            </div>
            <div className="font-impact text-3xl text-[#D90000] leading-none">+150€</div>
          </div>
          <div className="p-5 border border-white/10 bg-black flex items-center justify-between">
            <div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-white/45 mb-1">Berline / Break</div>
              <div className="text-white text-sm">Une passe supplémentaire</div>
            </div>
            <div className="font-impact text-3xl text-[#D90000] leading-none">+200€</div>
          </div>
          <div className="p-5 border border-white/10 bg-black flex items-center justify-between">
            <div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-white/45 mb-1">SUV / 4x4</div>
              <div className="text-white text-sm">Une passe supplémentaire</div>
            </div>
            <div className="font-impact text-3xl text-[#D90000] leading-none">+250€</div>
          </div>
        </div>
      </motion.div>
    </div>
  </section>
);

/* ============ GUARANTEE ============ */
const GuaranteeSection = () => (
  <section data-testid="ceram-guarantee" className="py-24 md:py-32 px-6 md:px-12 bg-[#0a0a0a]">
    <div className="max-w-[1500px] mx-auto">
      <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} className="max-w-3xl mb-16">
        <span className="text-[11px] uppercase tracking-[0.3em] text-[#D90000]">— Notre garantie</span>
        <h2 className="font-impact text-5xl md:text-6xl lg:text-[80px] leading-[0.9] mt-6">
          UNE GARANTIE ÉCRITE. <br />
          <span className="text-[#D90000]">PAS UNE PROMESSE.</span>
        </h2>
        <p className="text-white/60 mt-8 text-base md:text-lg leading-relaxed">
          Une garantie céramique n'a de valeur que si elle est écrite, nominative et conditionnée à un entretien
          documenté. C'est un standard exigé par les fabricants professionnels. Peu d'ateliers locaux l'appliquent
          réellement. Nous, oui.
        </p>
      </motion.div>

      <div className="grid md:grid-cols-3 gap-4 md:gap-6">
        {GUARANTEE.map((g, i) => {
          const Icon = g.icon;
          return (
            <motion.div
              key={g.title}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeUp}
              custom={i}
              data-testid={`ceram-guarantee-${i}`}
              className="p-8 md:p-10 border border-white/10 bg-black hover:border-[#D90000]/50 transition-colors"
            >
              <div className="w-14 h-14 flex items-center justify-center border border-[#D90000]/40 bg-[#D90000]/10 mb-6">
                <Icon size={24} className="text-[#D90000]" />
              </div>
              <h3 className="font-impact text-2xl md:text-3xl mb-4 leading-tight">{g.title}</h3>
              <p className="text-white/65 text-sm md:text-base leading-relaxed">{g.text}</p>
            </motion.div>
          );
        })}
      </div>

      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        variants={fadeUp}
        className="mt-12 p-8 border border-white/10 bg-[#050505]"
      >
        <div className="flex items-start gap-4">
          <ClipboardCheck size={22} className="text-[#D90000] shrink-0 mt-1" />
          <div>
            <div className="font-impact text-2xl mb-2">Les conditions d'entretien</div>
            <p className="text-white/70 leading-relaxed">
              La garantie est valable sous conditions : lavage main avec produits pH neutre, pas de station à rouleaux,
              contrôle annuel en atelier VistaWash, application d'un booster hydrophobe tous les 4 à 6 mois.
              Ces conditions vous sont expliquées à la remise du certificat, avec un kit d'entretien offert pour bien démarrer.
            </p>
          </div>
        </div>
      </motion.div>
    </div>
  </section>
);

/* ============ FAQ ============ */
const FaqSection = () => {
  const [open, setOpen] = useState(0);
  return (
    <section data-testid="ceram-faq" className="py-24 md:py-32 px-6 md:px-12">
      <div className="max-w-[1500px] mx-auto grid md:grid-cols-12 gap-12">
        <div className="md:col-span-4">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}>
            <span className="text-[11px] uppercase tracking-[0.3em] text-[#D90000]">— FAQ</span>
            <h2 className="font-impact text-4xl md:text-5xl lg:text-[64px] leading-[0.9] mt-6">
              QUESTIONS <br />
              <span className="text-[#D90000]">FRÉQUENTES</span>
            </h2>
            <p className="text-white/60 mt-6 leading-relaxed">
              Les vraies questions que se posent nos clients avant de franchir le pas.
              Des réponses honnêtes, sans jargon commercial.
            </p>
          </motion.div>
        </div>
        <div className="md:col-span-8">
          <div className="divide-y divide-white/5">
            {FAQ.map((item, i) => (
              <button
                key={i}
                type="button"
                onClick={() => setOpen(open === i ? -1 : i)}
                data-testid={`ceram-faq-${i}`}
                className="w-full text-left py-6 group"
              >
                <div className="flex items-start justify-between gap-6">
                  <h3 className="font-impact text-xl md:text-2xl leading-tight pr-4 uppercase tracking-wide">
                    {item.q}
                  </h3>
                  <div className="shrink-0 w-8 h-8 border border-white/20 group-hover:border-[#D90000] flex items-center justify-center text-white/60 group-hover:text-[#D90000] transition-colors">
                    {open === i ? <Minus size={14} /> : <Plus size={14} />}
                  </div>
                </div>
                <AnimatePresence>
                  {open === i && (
                    <motion.p
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                      className="text-white/65 leading-relaxed mt-4 max-w-3xl overflow-hidden"
                    >
                      {item.a}
                    </motion.p>
                  )}
                </AnimatePresence>
              </button>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

/* ============ FINAL CTA ============ */
const FinalCTA = () => (
  <section id="contact" data-testid="ceram-final-cta" className="py-24 md:py-32 px-6 md:px-12 bg-gradient-to-b from-[#1a0505] to-black border-t border-[#D90000]/20">
    <div className="max-w-[1500px] mx-auto grid md:grid-cols-12 gap-12">
      <div className="md:col-span-5">
        <span className="text-[11px] uppercase tracking-[0.3em] text-[#D90000]">— Passez à l'action</span>
        <h2 className="font-impact text-5xl md:text-6xl lg:text-[84px] leading-[0.9] mt-6 mb-8">
          DIAGNOSTIC <br />
          <span className="text-[#D90000]">GRATUIT</span> <br />
          DE VOTRE VÉHICULE.
        </h2>
        <p className="text-white/75 leading-relaxed max-w-md mb-10">
          Avant tout devis, nous examinons l'état de votre vernis sous éclairage LED et vous expliquons ce qu'il
          est possible d'obtenir. Une démarche transparente : vous savez exactement ce que vous payez et pourquoi.
        </p>
        <div className="flex flex-wrap gap-4 mb-10">
          <a
            href="#contact-form"
            data-testid="ceram-final-devis-cta"
            onClick={scrollToQuoteForm}
            className="inline-flex items-center gap-3 bg-[#D90000] hover:bg-white hover:text-black text-white text-[12px] uppercase tracking-[0.22em] font-bold px-8 py-5 transition-all border border-[#D90000] hover:border-white"
          >
            Réserver mon diagnostic <ArrowUpRight size={16} />
          </a>
          <a
            href={COMPANY.phone_link}
            data-testid="ceram-final-phone-cta"
            className="inline-flex items-center gap-3 border-2 border-white/30 hover:border-white hover:bg-white hover:text-black text-white text-[12px] uppercase tracking-[0.22em] font-bold px-8 py-5 transition-all"
          >
            <Phone size={16} /> {COMPANY.phone_display}
          </a>
        </div>
        <div className="grid grid-cols-2 gap-6 pt-8 border-t border-white/10">
          <div>
            <div className="text-[10px] uppercase tracking-[0.28em] text-[#D90000] mb-2">Atelier</div>
            <div className="text-white text-sm">{COMPANY.address_line1}</div>
            <div className="text-white/70 text-sm">{COMPANY.address_line2}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.28em] text-[#D90000] mb-2">Email</div>
            <a href={`mailto:${COMPANY.email}`} className="text-white text-sm hover:text-[#D90000] transition-colors">
              {COMPANY.email}
            </a>
          </div>
        </div>
      </div>

      <div className="md:col-span-7 bg-black/40 border border-white/10 p-8 md:p-10">
        <div className="text-[11px] uppercase tracking-[0.3em] text-[#D90000] mb-2">— Formulaire de diagnostic</div>
        <h3 className="font-impact text-3xl md:text-4xl mb-8 leading-tight">
          Parlez-nous de votre véhicule
        </h3>
        <QuoteForm defaultService="Protection Céramique" />
      </div>
    </div>
  </section>
);

export default Ceramique;
